import Styles from './InvitePeople.module.scss'
import { ShareSheetScreen, useShareSheetContext } from '../ShareSheetV2'
import useShareSheetV2 from 'hooks/useShareSheetV2'
import { Header } from '../Header/Header'
import { Heading } from 'blaze-ui/Heading/Heading'
import { RecipientSelector } from 'components/ShareSheet/RecipientSelector'
import { ContentComposer } from 'components/ShareSheet/ContentComposer/ContentComposer'
import { useRef, useState } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { lowerCase } from 'lodash'
import { Button } from 'blaze-ui/Button'
import { reviewTitle } from 'components/ReviewsWizard/reviewTitle'
import { ReviewType } from 'components/ReviewsWizard/types'
import { DueDatePicker } from 'components/DueDatePicker/DueDatePicker'
import { stopPropagation } from 'utils'
import dayjs from 'dayjs'
import { displayEndDatePickerValue } from 'utils/informalFeedbackRequest'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Calendar2, Trash2 } from 'blaze-ui/icons'
import { Permission, RecipientsSelectorValueType } from 'types'
import { InviteToWorkspace } from 'components/ShareSheet/InviteToWorkspace'
import { EmailValue } from 'components/ShareSheet/RecipientSelector/Types'
import { observer } from 'mobx-react-lite'
import { useReviewSubmit } from '../hooks/useReviewSubmit'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Text } from 'blaze-ui/Text/Text'

export const InvitePeople = observer(() => {
  const { setScreen, asset } = useShareSheetContext()
  const {
    recipients,
    setShareSheet,
    inviteMessage,
    inviteUsers,
    isSubmitting: isSubmittingShare,
    reviewScreenTitle,
    selectedPermission,
  } = useShareSheetV2(asset)
  const contentRef = useRef(null)
  const { closeAllModals } = useModals()
  const { isMobile } = useResponsiveDesign()
  const [dueDate, setDueDate] = useState<string>(dayjs().add(7, 'day').format('YYYY-MM-DD'))
  const [shareWithReview, setShareWithReview] = useState<boolean>(true)
  const [showInviteScreen, setShowInviteScreen] = useState<boolean>(false)
  const [inviteToWorkspace, setInviteToWorkspace] = useState<boolean>(false)
  const newRecipients = recipients.filter((rec) => rec.type === RecipientsSelectorValueType.Email)
  const displayText = (value: string | null) => dayjs(value as string).format('ddd, MMM D')

  const onBack = () => {
    setShareSheet({ recipients: [], inviteMessage: '' })
    setScreen(ShareSheetScreen.Default)
  }

  const { submit, isLoading: isSubmittingReview } = useReviewSubmit({
    almanacFile: asset,
    onSuccess: (request) => {
      setScreen(ShareSheetScreen.ReviewSent)
    },
  })

  const submitReview = ({ addMembers }: { addMembers: boolean }) => {
    if (!asset) return

    submit({
      addMembers,
      recipients: recipients,
      title: reviewTitle(asset.title, ReviewType.FEEDBACK),
      message: inviteMessage,
      htmlMessage: inviteMessage,
      endDate: dueDate,
      permission: selectedPermission as 'edit' | 'comment' | 'read',
    })

    setShareSheet({ inviteMessage: '' })
  }

  const handleSubmitReview = () => {
    if (newRecipients.length > 0) {
      setShowInviteScreen(true)

      return
    }

    submitReview({ addMembers: false })
  }

  const handleSubmitShare = () => {
    if (newRecipients.length > 0) {
      setScreen(ShareSheetScreen.ShowInviteToWorkspaceScreen)
      setShareSheet({ showInviteToWorkspaceScreen: true })

      return
    }

    inviteUsers().then(() => closeAllModals())
  }

  const handleSendInvite = shareWithReview ? handleSubmitReview : handleSubmitShare
  const title = reviewScreenTitle || 'Invite people to review your work'

  if (showInviteScreen) {
    return (
      <InviteToWorkspace
        inviteToWorkspace={inviteToWorkspace}
        setInviteToWorkspace={setInviteToWorkspace}
        goBack={() => setShowInviteScreen(false)}
        onSubmit={() => submitReview({ addMembers: inviteToWorkspace })}
        isSubmitting={isSubmittingShare || isSubmittingReview}
        newRecipients={newRecipients as EmailValue[]}
      />
    )
  }

  return (
    <div className={Styles.container}>
      <Header onBack={onBack}>
        <Heading level={3}>{title}</Heading>
      </Header>
      <div className={Styles.body}>
        <RecipientSelector
          file={asset}
          showLabel={false}
          accessDropdownVariant="ghost"
          withSuggestedReviewers
          accessDropdownOptions={asset.type === 'article' ? [Permission.Edit, Permission.Comment] : undefined}
          secondaryAction={
            shareWithReview ? undefined : (
              <div className={Styles.addDueDate}>
                <Button variant="ghost" frontIcon={Calendar2} onPress={() => setShareWithReview(true)}>
                  Add Due Date
                </Button>
              </div>
            )
          }
        />

        {shareWithReview && (
          <>
            <ResponsiveDesign.Mobile>
              <div className={Styles.reviewLabel}>
                <Text variant="metadata" color="var(--dark-60)">
                  Review by:
                </Text>
              </div>
            </ResponsiveDesign.Mobile>
            <div className={Styles.dueDateContainer} onClick={stopPropagation}>
              <ResponsiveDesign.Desktop>
                <span className={Styles.dueDate}>Review by:</span>
              </ResponsiveDesign.Desktop>
              <DueDatePicker
                value={dueDate}
                onChange={(value) => value && setDueDate(value)}
                displayText={displayText}
                minValue={dayjs().format('YYYY-MM-DD')}
                shape="squared"
                size="lg"
                variant="ghost"
                className={Styles.dueDatePicker}
                frontIcon={isMobile ? Calendar2 : undefined}
              />
              <span className={Styles.dueDate}>{lowerCase(displayEndDatePickerValue(dueDate))}</span>
              <div className={Styles.removeDueDate}>
                <IconButton
                  aria-label="Remove Due Date"
                  icon={Trash2}
                  variant="ghost"
                  onPress={() => setShareWithReview(false)}
                />
              </div>
            </div>
          </>
        )}
        <div className={Styles.messageWrapper}>
          <ContentComposer
            ref={contentRef}
            content={inviteMessage}
            placeholder="Type your message here (optional)..."
            onChange={(inviteMessage) => setShareSheet({ inviteMessage })}
            size="md"
          />
        </div>
      </div>
      <ResponsiveDesign.Mobile>
        <Button
          size="xl"
          fullWidth
          variant="primary"
          onPress={handleSendInvite}
          isDisabled={!recipients.length || isSubmittingReview || isSubmittingShare}
        >
          Send Invite
        </Button>
      </ResponsiveDesign.Mobile>
      <ResponsiveDesign.Desktop>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Button size="lg" variant="tertiary" onPress={onBack} isDisabled={isSubmittingReview}>
              Cancel
            </Button>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <Button
              size="lg"
              variant="primary"
              onPress={handleSendInvite}
              isDisabled={!recipients.length || isSubmittingReview || isSubmittingShare}
            >
              Send Invite
            </Button>
          </Modal.FooterContent>
        </Modal.Footer>
      </ResponsiveDesign.Desktop>
    </div>
  )
})
